import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { VocabularyTestHistory, VocabularyTestTopic } from 'app/types';
import { fmt } from 'pages/VocabularyTest/cefr';
import FilledActionButton from 'components/FilledActionButton';

interface VocabularyTestInProgressBannerProps {
  inProgressTest: VocabularyTestHistory;
  testTopics: VocabularyTestTopic[];
  onContinue: () => void;
  onStartFresh: () => void;
  starting?: boolean;
}

interface ParsedWordList {
  wordSet: string;
  category: string;
  subCategories: string[];
}

// BE formats a custom scope entry as "<wordSet> / <category>: <sub1, sub2>"
// (plain "<wordSet>" with no " / " when the scope is the default, uncustomized
// whole list — see TestVocabularyServiceImpl#getWordLists). Split that back
// into parts so the banner can show word-set/category as headings instead of
// one long run-on sentence of every subcategory.
const parseWordListEntry = (entry: string): ParsedWordList => {
  const slashIndex = entry.indexOf(' / ');
  if (slashIndex === -1) {
    return { wordSet: entry, category: '', subCategories: [] };
  }
  const wordSet = entry.slice(0, slashIndex);
  const rest = entry.slice(slashIndex + 3);
  const colonIndex = rest.indexOf(': ');
  if (colonIndex === -1) {
    return { wordSet, category: rest, subCategories: [] };
  }
  return {
    wordSet,
    category: rest.slice(0, colonIndex),
    subCategories: rest.slice(colonIndex + 2).split(', '),
  };
};

// Groups entries by word-set name (e.g. B1 and B2 can both belong to
// "Англійська лексика за рівнями"), preserving first-seen order.
const groupWordListsByWordSet = (wordLists: string[]) => {
  const order: string[] = [];
  const byWordSet = new Map<string, ParsedWordList[]>();
  wordLists.forEach((entry) => {
    const parsed = parseWordListEntry(entry);
    if (!byWordSet.has(parsed.wordSet)) {
      order.push(parsed.wordSet);
      byWordSet.set(parsed.wordSet, []);
    }
    byWordSet.get(parsed.wordSet)?.push(parsed);
  });
  return order.map((wordSet) => ({
    wordSet,
    items: byWordSet.get(wordSet) || [],
  }));
};

const TestScopeSummary: React.FC<{ wordLists: string[] }> = ({
  wordLists,
}) => (
  <Stack spacing={1}>
    {groupWordListsByWordSet(wordLists).map(({ wordSet, items }) => (
      <Box key={wordSet}>
        <Typography sx={{ fontSize: 14, fontWeight: 800 }}>
          {wordSet}
        </Typography>
        {items.map(
          (item) =>
            item.category && (
              <Typography
                key={item.category}
                sx={{
                  color: 'text.secondary',
                  fontSize: 13.5,
                  lineHeight: 1.5,
                  pl: 1.75,
                }}
              >
                <Box
                  component={'span'}
                  sx={{ color: 'text.primary', fontWeight: 700 }}
                >
                  {item.category}
                </Box>
                {item.subCategories.length > 0 &&
                  `: ${item.subCategories.join(', ')}`}
              </Typography>
            ),
        )}
      </Box>
    ))}
  </Stack>
);

const VocabularyTestInProgressBanner: React.FC<
  VocabularyTestInProgressBannerProps
> = ({ inProgressTest, testTopics, onContinue, onStartFresh, starting }) => {
  // Same "done" definition the runner uses (passed + the one currently being
  // answered count toward progress) — keeps the percentage consistent across
  // the landing banner and the runner's own progress bar.
  const passedCount = testTopics.filter(
    (topic) => topic.status === 'PASSED',
  ).length;
  const currentCount = testTopics.filter(
    (topic) => topic.status === 'CURRENT',
  ).length;
  const totalTopics = testTopics.length;
  const progressPct = totalTopics
    ? Math.round(((passedCount + currentCount) / totalTopics) * 100)
    : 0;
  const wordLists = inProgressTest.wordLists || [];

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'primary.main',
        borderRadius: 2,
        boxShadow: '0 14px 34px -24px rgba(59,44,147,0.45)',
        p: { xs: 2.5, md: '24px 28px' },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent={'space-between'}
        spacing={{ xs: 2, md: 3.5 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction={'row'} alignItems={'center'} spacing={1} sx={{ mb: 0.75 }}>
            <Box
              sx={{
                bgcolor: 'primary.main',
                borderRadius: '50%',
                height: 8,
                width: 8,
              }}
            />
            <Typography
              sx={{
                color: 'primary.main',
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              {'Тест у процесі'}
            </Typography>
          </Stack>
          <Typography
            sx={{
              fontSize: { xs: 18, md: 22 },
              fontWeight: 800,
              lineHeight: 1.2,
              mb: 0.5,
            }}
          >
            {'Продовжити незавершений тест'}
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
            {'Вивчено слів: '}
            <Box component={'span'} sx={{ color: 'primary.main' }}>
              {fmt(inProgressTest.learnedCount)}
            </Box>
          </Typography>
          {!!wordLists.length && (
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>
                {'Обрані категорії:'}
              </Typography>
              <TestScopeSummary wordLists={wordLists} />
            </Box>
          )}
        </Box>

        <Stack
          direction={'row'}
          spacing={1.25}
          sx={{ flex: 'none', flexWrap: 'wrap', rowGap: 1 }}
        >
          <Button
            variant={'outlined'}
            onClick={onStartFresh}
            disabled={starting}
            sx={{
              borderColor: 'error.main',
              color: 'error.main',
              height: 46,
              px: 2.75,
              '&:hover': {
                backgroundColor: 'rgba(211,47,47,0.08)',
                borderColor: 'error.main',
                color: 'error.main',
              },
            }}
          >
            {'Почати новий'}
          </Button>
          <FilledActionButton
            startIcon={<PlayArrowIcon />}
            onClick={onContinue}
            disabled={starting}
            sx={{ height: 46, px: 2.75 }}
          >
            {'Продовжити тест'}
          </FilledActionButton>
        </Stack>
      </Stack>

      {totalTopics > 0 && (
        <Stack
          direction={'row'}
          alignItems={'center'}
          spacing={1.5}
          sx={{ mt: { xs: 2, md: '18px' } }}
        >
          <Box
            sx={{
              bgcolor: 'primary.purple',
              borderRadius: 999,
              flex: 1,
              height: 10,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                bgcolor: 'primary.main',
                height: '100%',
                width: `${progressPct}%`,
              }}
            />
          </Box>
          <Typography
            sx={{
              color: 'primary.main',
              fontSize: 15,
              fontWeight: 800,
              minWidth: 44,
              textAlign: 'right',
            }}
          >
            {`${progressPct}%`}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

export default VocabularyTestInProgressBanner;
